/**
 * Templates — what the firm says (FR-K2). Each template is a row; editing one
 * is a kit `Dialog` (C14 §10.6 item 7), where before Wave 15 it unfolded in
 * place. `settings:can_update` hides every write; without it the dialog shows
 * the wording read-only.
 */

import { Badge, Button, Dialog, EmptyState, Input, Textarea } from '@fiveox/ui'
import { Mail, Send } from 'lucide-react'
import { useEffect, useState } from 'react'

import { ApiError } from '../../lib/api'
import { type TemplateRecord, comms, explainReason } from '../../lib/api/comms'
import type { Act } from './shared'

function TemplateDialog({
  template,
  mayEdit,
  onClose,
  onSaved,
}: {
  template: TemplateRecord | null
  mayEdit: boolean
  onClose: () => void
  onSaved: (said: string) => Promise<void>
}) {
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState<'save' | 'reset' | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!template) return
    setSubject(template.subject ?? '')
    setBody(template.body)
    setError(null)
  }, [template])

  if (!template) return null

  const run = async (kind: 'save' | 'reset') => {
    setBusy(kind)
    setError(null)
    try {
      if (kind === 'save') await comms.updateTemplate(template.id, { subject, body })
      else await comms.resetTemplate(template.id)
      await onSaved(kind === 'save' ? 'Saved.' : 'Put back as shipped.')
      onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <Dialog open onClose={onClose} title={template.name} error={error} width="lg">
      <div className="space-y-4">
        {/* The key and the code, both, because they are two different things
            and a firm debugging a send needs the second one. */}
        <p className="text-muted-foreground font-mono text-xs">
          {template.key}
          {template.code !== template.key && <> → {template.code}</>}
        </p>
        <Input
          label="Subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          disabled={!mayEdit}
        />
        <Textarea
          label="Message"
          rows={8}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={!mayEdit}
          hint={
            template.variables.length > 0
              ? `You can use: ${template.variables.map((v) => `{{${v}}}`).join(', ')}`
              : undefined
          }
        />
        {!mayEdit && (
          <p className="text-muted-foreground text-sm">
            Your role can read the wording but not change it.
          </p>
        )}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          {mayEdit && template.source === 'firm' && (
            <Button
              variant="ghost"
              onClick={() => void run('reset')}
              loading={busy === 'reset'}
              disabled={busy !== null}
              className="sm:mr-auto"
            >
              Restore the shipped wording
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} disabled={busy !== null}>
            {mayEdit ? 'Cancel' : 'Close'}
          </Button>
          {mayEdit && (
            <Button onClick={() => void run('save')} loading={busy === 'save'} disabled={busy !== null}>
              Save
            </Button>
          )}
        </div>
      </div>
    </Dialog>
  )
}

export function TemplateList({
  templates,
  mayEdit,
  act,
  onSaved,
}: {
  templates: TemplateRecord[]
  mayEdit: boolean
  act: Act
  onSaved: (said: string) => Promise<void>
}) {
  const [open, setOpen] = useState<string | null>(null)

  if (templates.length === 0) {
    return <EmptyState variant="outline" title="No templates yet" />
  }

  const opened = templates.find((t) => t.id === open) ?? null

  return (
    <>
      <ul className="border-border divide-border divide-y rounded-2xl border bg-card shadow-sm">
        {templates.map((t) => (
          <li key={t.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-4 sm:px-5">
            <div className="min-w-0 flex-1">
              <p className="text-foreground flex flex-wrap items-center gap-2 text-sm font-medium">
                <Mail size={14} aria-hidden className="text-muted-foreground" /> {t.name}
                {t.source === 'firm' ? <Badge tone="info">Yours</Badge> : <Badge>As shipped</Badge>}
                {!t.active && <Badge tone="warning">Off</Badge>}
              </p>
              <p className="text-muted-foreground mt-1 truncate font-mono text-xs">
                {t.key}
                {t.code !== t.key && <> → {t.code}</>}
              </p>
              {t.description && <p className="text-muted-foreground mt-1 text-xs">{t.description}</p>}
            </div>
            <div className="flex shrink-0 gap-2">
              <Button variant="outline" size="sm" onClick={() => setOpen(t.id)}>
                {mayEdit ? 'Edit' : 'Read'}
              </Button>
              {mayEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    act(async () => {
                      const { data } = await comms.sendTest(t.id)
                      if (!data.sent) {
                        throw new ApiError('NOT_SENT', explainReason(data.delivery.reason), 200)
                      }
                    }, 'Sent to your firm’s own address.')
                  }
                >
                  <Send size={14} aria-hidden /> Test
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
      <TemplateDialog
        template={opened}
        mayEdit={mayEdit}
        onClose={() => setOpen(null)}
        onSaved={onSaved}
      />
    </>
  )
}
